import React from "react";
import AdminLayout from "../../../components/AdminLayout";
import { Button, Table, Tabs, Tag, Input, Modal, type TableProps, type TabsProps, Dropdown, Popover, Alert } from "antd";
import { DeleteOutlined, LockOutlined, MoreOutlined, SearchOutlined, ExclamationCircleOutlined, UndoOutlined } from "@ant-design/icons";
import "./index.css";
import { useContentManagement } from "./hooks/useContentManagement";
import { formatThaiDate } from "../../../utils/dateFormatter";
import { contentService, type ContentVersion } from "../../../services/contentService";
import { useUserInfo } from "../../../hooks/useUserInfo";

export interface ContentDataType {
  key: string;
  sk: string;
  dxNumber: string;
  title: string;
  creator: string;
  createdAt: string;
  updatedAt: string;
  updatedBy: string;
  version: string;
  status: string;
  edit_status: string;
  content_owner: string;
  content_members: string[];
}

const ContentManagement: React.FC = () => {
  // API integration
  const {
    tableData: allTableData,
    isLoading,
    isDeleting,
    isUpdating,
    error,
    deleteContent,
    republishContent,
    unpublishContent,
    refreshData,
    clearError,
    messageContext
  } = useContentManagement();

  // User info hook
  const { userInfo, isLoading: isLoadingUserInfo } = useUserInfo();

  // Local state
  const [searchText, setSearchText] = React.useState("");
  const [versionData, setVersionData] = React.useState<ContentVersion[]>([]);
  const [isLoadingVersions, setIsLoadingVersions] = React.useState(false);
  const [deleteModalVisible, setDeleteModalVisible] = React.useState(false);
  const [versionModalVisible, setVersionModalVisible] = React.useState('');
  const [publishModalVisible, setPublishModalVisible] = React.useState<ContentVersion | null>(null);
  const [itemToDelete, setItemToDelete] = React.useState<ContentDataType | null>(null);
  const [currentFilter, setCurrentFilter] = React.useState<string>('');

  // Determine user role and permissions
  const userRole = React.useMemo(() => {
    if (!userInfo?.roles || userInfo.roles.length === 0) return null;
    
    if (userInfo.roles.includes('super-admin')) return 'super-admin';
    if (userInfo.roles.includes('admin')) return 'admin';
    if (userInfo.roles.includes('content-owner')) return 'content-owner';
    if (userInfo.roles.includes('content-member')) return 'content-member';
    
    return null;
  }, [userInfo]);

  const userEmail = userInfo?.email;

  // Check if user can delete content (only super-admin)
  const canDeleteContent = userRole === 'super-admin';

  // Client-side filtered data
  const tableData = React.useMemo(() => {
    let filtered = allTableData;

    // Apply role-based filtering
    // Note: A user can be both content-owner for some content AND content-member for others
    if ((userRole === 'content-owner' || userRole === 'content-member') && userEmail) {
      filtered = filtered.filter(item => {
        // Show if user is the owner
        const isOwner = item.content_owner === userEmail;
        // Show if user is a member
        const isMember = item.content_members && item.content_members.includes(userEmail);
        
        return isOwner || isMember;
      });
    }
    // super-admin and admin can see all content, so no filtering needed

    // Apply status filter
    if (currentFilter === 'progress') {
      filtered = filtered.filter(item => item.status === 'draft' || item.status === 'progress');
    } else if (currentFilter === 'published') {
      filtered = filtered.filter(item => item.status === 'published');
    }

    // Apply search filter
    if (searchText) {
      const searchLower = searchText.toLowerCase();
      filtered = filtered.filter(item =>
        item.key.toLowerCase().includes(searchLower) ||
        item.title.toLowerCase().includes(searchLower) ||
        item.creator.toLowerCase().includes(searchLower) ||
        item.updatedBy.toLowerCase().includes(searchLower)
      );
    }

    return filtered;
  }, [allTableData, currentFilter, searchText, userRole, userEmail]);

  // Calculate filtered counts based on user role
  const filteredCounts = React.useMemo(() => {
    let roleFilteredData = allTableData;

    // Apply role-based filtering
    // Note: A user can be both content-owner for some content AND content-member for others
    if ((userRole === 'content-owner' || userRole === 'content-member') && userEmail) {
      roleFilteredData = roleFilteredData.filter(item => {
        // Show if user is the owner
        const isOwner = item.content_owner === userEmail;
        // Show if user is a member
        const isMember = item.content_members && item.content_members.includes(userEmail);
        
        return isOwner || isMember;
      });
    }

    const total = roleFilteredData.length;
    const progress = roleFilteredData.filter(item => item.status === 'draft' || item.status === 'progress').length;
    const published = roleFilteredData.filter(item => item.status === 'published').length;

    return { total, progress, published };
  }, [allTableData, userRole, userEmail]);

  const handleDeleteClick = (item: ContentDataType) => {
    setItemToDelete(item);
    setDeleteModalVisible(true);
  };

  const handleDeleteConfirm = async () => {
    if (itemToDelete) {
      const success = await deleteContent(itemToDelete);
      if (success) {
        setDeleteModalVisible(false);
        setItemToDelete(null);
      }
    }
  };

  const handleDeleteCancel = () => {
    setDeleteModalVisible(false);
    setItemToDelete(null);
  };

  const handleUnpublish = async (item: ContentDataType) => {
    await unpublishContent(item);
  };

  const handleTabChange = (activeKey: string) => {
    setCurrentFilter(activeKey === 'overview' ? '' : activeKey);
  };

  const fetchVersions = async (sk: string) => {
    setIsLoadingVersions(true);
    try {
      const response = await contentService.getContentVersions(sk);
      // get first 5 versions
      const versions = response.versions.slice(0, 5);
      setVersionData(versions);
    } catch (error) {
      console.error('Failed to fetch versions:', error);
    } finally {
      setIsLoadingVersions(false);
    }
  };

  const handleVersionClick = async (record: ContentDataType) => {
    setVersionModalVisible(record.sk);
    await fetchVersions(record.sk);
  };

  const contentColumns: TableProps<ContentDataType>['columns'] = [
    {
      title: 'รหัส',
      dataIndex: 'key',
      key: 'key',
      render: (text: string) => <p>{text}</p>,
      fixed: 'left',
      width: 100,
    },
    {
      title: 'หัวข้อ',
      dataIndex: 'title',
      key: 'title',
      render: (text: string, record: ContentDataType) => (
        <Popover content={
          <div style={{ maxWidth: 300 }}>
            <p style={{ color: 'white' }}>{text}</p>
          </div>
        } color={"rgba(250, 140, 22, 1)"}>
          <a
            style={{ color: "rgba(250, 140, 22, 1)" }}
            onClick={() => window.location.href = `/admin/content-list/${record.sk}${record.status === 'published' ? `?version=v${record.version}` : ''}`}
            href={`/admin/content-list/${record.sk}${record.status === 'published' ? `?version=v${record.version}` : ''}`}
          >
            {text}
          </a>
        </Popover>
      ),
      fixed: 'left',
      width: 400,
    },
    {
      title: 'ผู้รับผิดชอบหลัก',
      dataIndex: 'creator',
      key: 'creator',
      render: (text) => <p>{text}</p>,
    },
    {
      title: 'วันที่สร้าง',
      dataIndex: 'createdAt',
      key: 'createdAt',
      render: (text) => <p>{formatThaiDate(text)}</p>,
    },
    {
      title: 'วันที่แก้ไขล่าสุด',
      dataIndex: 'updatedAt',
      key: 'updatedAt',
      render: (text) => <p>{formatThaiDate(text)}</p>,
    },
    {
      title: 'ผู้ที่แก้ไขล่าสุด',
      dataIndex: 'updatedBy',
      key: 'updatedBy',
      render: (text) => <p>{text}</p>,
    },
    {
      title: 'สถานะ',
      dataIndex: 'status',
      key: 'status',
      render: (_, { status }) => {
        let color = '';
        let text = '';

        if (status === 'published') {
          color = 'green';
          text = 'เผยแพร่แล้ว';
        } else if (status === 'draft' || status === 'progress') {
          color = 'orange';
          text = 'แบบร่าง';
        }

        return <Tag color={color}>{text}</Tag>;
      },
    },
    {
      title: 'เวอร์ชัน',
      dataIndex: 'version',
      key: 'version',
      render: (text, record) => {
        return (
          <a
            style={{ color: "rgba(250, 140, 22, 1)" }}
            onClick={() => handleVersionClick(record)}
          >
            {record.status === 'published' ? `V.${text}` : '-'}
          </a>
        );
      },
    },
    {
      title: 'จัดการ',
      key: 'edit',
      render: (_, record) => {
        const menuItems = [];

        // Add delete option only for super-admin users and draft/progress content
        if (canDeleteContent && (record.status === 'draft' || record.status === 'progress')) {
          menuItems.push({
            key: 'delete',
            label: 'ลบรายการ',
            icon: <DeleteOutlined />,
            onClick: () => handleDeleteClick(record),
            disabled: isDeleting,
          });
        }

        // Add unpublish option for published content
        if (record.status === 'published') {
          menuItems.push({
            key: 'unpublish',
            label: 'ยกเลิกการเผยแพร่',
            icon: <LockOutlined />,
            danger: true,
            onClick: () => handleUnpublish(record),
            disabled: isUpdating,
          });
        }

        // If no menu items available, show disabled button
        if (menuItems.length === 0) {
          return (
            <Button 
              icon={<MoreOutlined />} 
              type="text" 
              disabled 
              title="ไม่มีการดำเนินการที่สามารถทำได้"
            />
          );
        }

        return (
          <div>
            <Dropdown
              menu={{ items: menuItems }}
              trigger={['click']}
            >
              <Button icon={<MoreOutlined />} type="text" disabled={isDeleting || isUpdating} />
            </Dropdown>
          </div>
        );
      },
    },
  ];

  const versionColumns: TableProps<ContentVersion>['columns'] = [
    {
      title: 'เวอร์ชัน',
      dataIndex: 'version',
      key: 'version',
      render: (text) => <p>{text}</p>,
    },
    {
      title: 'หัวข้อ',
      dataIndex: 'title_th',
      key: 'title_th',
      render: (text: string, record: ContentVersion) => (
        <Popover content={
          <div style={{ maxWidth: 300 }}>
            <p style={{ color: 'white' }}>{text}</p>
          </div>
        } color={"rgba(250, 140, 22, 1)"}>
          <a
            style={{ color: "rgba(250, 140, 22, 1)" }}
            onClick={() => window.location.href = `/admin/content-list/${versionModalVisible}?version=${record.version}`}
            href={`/admin/content-list/${versionModalVisible}?version=${record.version}`}
          >
            {text}
          </a>
        </Popover>
      ),
    },
    {
      title: 'วันที่เผยแพร่',
      dataIndex: 'publish_timestamp',
      key: 'publish_timestamp',
      render: (text) => <p>{formatThaiDate(text)}</p>,
    },
    {
      title: 'ผู้เผยแพร่',
      dataIndex: 'publisher_name',
      key: 'publisher_name',
      render: (text) => <p>{text}</p>,
    },
    {
      title: 'จัดการ',
      key: 'edit',
      render: (_, record) => (
        <div>
          <Dropdown
            menu={{
              items: [
                {
                  key: 'publish',
                  label: 'เผยแพร่',
                  icon: <UndoOutlined />,
                  onClick: () => handleRepublishClick(record),
                }
              ],
            }}
            trigger={['click']}
          >
            <Button icon={<MoreOutlined />} type="text" />
          </Dropdown>
        </div>
      ),
    },
  ];

  const contentTable = () => {
    // Show loading state if either user info or content is loading
    const isTableLoading = isLoading || isLoadingUserInfo;
    
    // Only show data when both user info and content are loaded
    const safeTableData = isLoadingUserInfo ? [] : tableData;

    return (
      <div>
        <div style={{ marginBottom: 16, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div style={{ display: 'flex', gap: 8 }}>
            {error && (
              <Alert
                message="Error loading data"
                description={error}
                type="error"
                closable
                onClose={clearError}
                style={{ flex: 1, maxWidth: 400 }}
              />
            )}
          </div>
          <Input
            placeholder="ค้นหารายการ"
            prefix={<SearchOutlined style={{ color: 'gray' }} />}
            value={searchText}
            onChange={(e) => setSearchText(e.target.value)}
            allowClear
            style={{ width: 300 }}
          />
        </div>
        <Table
          columns={contentColumns}
          dataSource={safeTableData}
          loading={isTableLoading}
          scroll={{ x: 1200 }}
          pagination={{
            showSizeChanger: true,
            showTotal: (total, range) => `${range[0]}-${range[1]} of ${total} items`,
            defaultPageSize: 20,
            pageSizeOptions: ['10', '20', '50', '100']
          }}
        />
      </div>
    );
  }

  const tabs: TabsProps['items'] = [
    {
      key: 'overview',
      label: 'รายการทั้งหมด' + (filteredCounts.total > 0 ? ` (${filteredCounts.total})` : ''),
      children: contentTable(),
    },
    {
      key: 'progress',
      label: 'แบบร่าง' + (filteredCounts.progress > 0 ? ` (${filteredCounts.progress})` : ''),
      children: contentTable(),
    },
    {
      key: 'published',
      label: 'เผยแพร่แล้ว' + (filteredCounts.published > 0 ? ` (${filteredCounts.published})` : ''),
      children: contentTable(),
    },
  ];

  const versionModal = () => {
    return (
      <Modal
        title="ประวัติการเผยแพร่"
        open={!!versionModalVisible}
        onCancel={() => {
          setVersionModalVisible('');
          setVersionData([]); // Clear version data when modal closes
        }}
        centered
        footer={null}
        width={{
          xs: '90%',
          sm: '80%',
          md: '70%',
          lg: '60%',
          xl: '50%',
          xxl: '40%',
        }}
      >
        <Table
          columns={versionColumns}
          dataSource={versionData}
          loading={isLoadingVersions}
          pagination={false}
        />
      </Modal>
    );
  }

  const handleRepublishClick = (record: ContentVersion) => {
    setPublishModalVisible(record);
  }

  const republishVersion = async(record: ContentVersion) => {
    if (publishModalVisible) {
      const success = await republishContent(versionModalVisible, record.version);
      if (success) {
        setPublishModalVisible(null);
        setVersionModalVisible('');
        setVersionData([]);
        // Refresh content list to show updated data
        await refreshData();
      }
    }
  }

    return (
      <AdminLayout children={
        <>
          {messageContext}
          <div>
            <div className="header-container">
              <h3>ดูรายการคอนเทนต์</h3>
            </div>
            
            <Tabs defaultActiveKey="overview" items={tabs} onChange={handleTabChange} />          {versionModal()}
          <Modal
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 16 }}>
                <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '20px' }} />
                <span>ยืนยันการลบรายการ</span>
              </div>
            }
            open={deleteModalVisible}
            onOk={handleDeleteConfirm}
            onCancel={handleDeleteCancel}
            okText="ยืนยัน"
            cancelText="ยกเลิก"
            centered
          >
            <p>คุณต้องการลบรายการ {itemToDelete?.key}: {itemToDelete?.title} หรือไม่?</p>
          </Modal>
          <Modal
            title={
              <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: 16, height: 32 }}>
                <ExclamationCircleOutlined style={{ color: '#faad14', fontSize: '20px' }} />
                <span>ต้องการเผยแพร่คอนเทนต์นี้แทนที่เวอร์ชันปัจจุบันใช่หรือไม่?</span>
              </div>
            }
            open={publishModalVisible !== null}
            onOk={() => {
              if (publishModalVisible !== null) {
                republishVersion(publishModalVisible);
              }
              else {
                setPublishModalVisible(null);
              }
            }}
            onCancel={() => setPublishModalVisible(null)}
            okText="ยืนยัน"
            cancelText="ยกเลิก"
            centered
          >
            <p>คอนเทนต์เวอร์ชันนี้จะถูกแสดงบน Website ทันทีแทนเวอร์ชันปัจจุบัน</p>
          </Modal>
        </div>
      </>
    } breadcrumb={{
      items: [{ title: "รายการคอนเทนต์" }, { title: "ดูรายการคอนเทนต์" }],
    }}>
    </AdminLayout>
  );
};

export default ContentManagement;
